import dayjs from 'dayjs';
import { useStoreActions } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Dialog } from '@ge/components/modal';
import { usePostManualAnnotationImage } from '@ge/feat-inspections/data-hooks/use-inspections';
import { FormMode } from '@ge/models/constants';
import { FileType } from '@ge/models/constants';
import { AttachmentField } from '@ge/shared/components/meta-fields';

const AllowedFileTypes = [FileType.JPEG, FileType.JPG, FileType.PNG];

const AttachmentContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 4px;
    height: 0;
  }

  ::-webkit-scrollbar-track {
    background: ${(props) => props.theme.scrollbar.trackBackground};
  }

  ::-webkit-scrollbar-thumb:active {
    background: ${(props) => props.theme.scrollbar.thumbActiveBackground};
  }

  ::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.scrollbar.thumbBackground};
    border-radius: 2.5px;
  }
`;

const FooterDivider = styled.div`
  border-top: 1px solid black;
  margin: 20px -26px 0px;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const FooterButtons = styled.div`
  margin-left: auto;
  margin-top: 15px;
  button:not(:last-of-type) {
    margin-right: 25px;
  }
`;

const UploadDamageImageDialog = ({ isOpen, onClose, damageDetails }) => {
  const [uploadTemplateMode] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: postManualAnnotationImage } = usePostManualAnnotationImage();

  const { getPresignedUrl, putFilesByPresignedUrl } = useStoreActions((actions) => actions.files);

  const { t } = useTranslation(['general', 'tasks']);

  const handleCloseDialog = () => {
    if (!isLoading) {
      setAttachments([]);
      onClose(false);
    }
  };

  const handleUpload = useCallback(
    (selectedFiles) => {
      setAttachments(selectedFiles);
    },
    [setAttachments],
  );

  const handleUploadTemplate = async () => {
    const speedWindow = window.open('', '', `width=600,height=${String(24 * 7)}`);
    if (speedWindow) {
      speedWindow.document.writeln(
        'Uploading <span id="uploadProgressDamage">0</span>% complete<br /><br /><br />',
      );
      speedWindow.document.title = 'File upload in progress';
    } else {
      console.log("Can't create Speed Window. Probably a popup blocker stops it.");
    }

    const uploadProgress = (p) => {
      if (speedWindow) {
        speedWindow.document.querySelector('#uploadProgressDamage').textContent  = (
          (p.loaded / p.total) *
          100
        ).toFixed(2);
      }
    };

    const uploadInDBProgress = () => {
      if (speedWindow) {
        speedWindow.document.writeln(
          'Saving the uploaded file in the database and refreshing the image',
        );
      }
    };

    if (attachments.length === 1) {
      const annotationData = damageDetails.annotationResult.data;
      const currDate = dayjs();
      const damageId = annotationData.id;
      const component = annotationData.attributes.component;
      const jobId = annotationData.attributes.job_id;
      const assetSiteId = damageDetails.asset_site_id;
      const formattedDate = currDate.format('YYYY-MM-DD');
      const unixTimestamp = currDate.unix();
      const fileName = attachments[0].file.name;

      // Construct the filename step by step
      const filename =
        `${component}/` +
        `${assetSiteId}/` +
        `${damageId}/` +
        `${formattedDate}/` +
        `${unixTimestamp}/` +
        `output/` +
        `${fileName}`;
      const expires = 60 * 60 * 12;
      const data = {
        files: [filename.replace(/,/g, '_COMMA_')],
        type: 'put',
        expires,
      };
      setIsLoading(true);
      getPresignedUrl(data).then((signedUrls) => {
        putFilesByPresignedUrl({
          presignedUrl: signedUrls[0].url,
          fileData: attachments[0].file,
          uploadProgress,
        })
          .then(() => {
            // API call to DB
            const body = {
              damageid: damageId,
              job_id: jobId,
              imgname: filename,
            };
            uploadInDBProgress();
            postManualAnnotationImage(body, {
              onSettled: () => {
                setTimeout(() => {
                  if (speedWindow) {
                    speedWindow.close();
                  }
                }, 5000);
                setIsLoading(false);
                onClose(true);
              },
            });
          })
          .catch((error) => {
            console.error(error);
          });
      });
      setAttachments([]);
    }
  };

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={handleCloseDialog}
        header={t('damages.upload_image', 'Upload Image')}
      >
        <AttachmentContainer>
          <AttachmentField
            id="damage-image-attachment"
            name="attachment"
            mode={FormMode.CREATE}
            collapsed={false}
            hideCollapsiblePanel={true}
            handleUpload={handleUpload}
            maxFiles={1}
            maxSize={'5MB'}
            FileType={'JPEG, PNG or JPG'}
            maxFileSizeMb={5}
            files={attachments}
            uploadTemplateMode={uploadTemplateMode}
            allowedFileTypes={AllowedFileTypes}
          />
        </AttachmentContainer>

        <FooterDivider></FooterDivider>
        <FooterContainer>
          <FooterButtons>
            <Button onClick={handleCloseDialog} disabled={isLoading}>
              {t('general:cancel', 'Cancel')}
            </Button>
            <Button primary onClick={handleUploadTemplate} disabled={attachments.length !== 1}>
              {t('general:upload', 'Upload')}
            </Button>
          </FooterButtons>
        </FooterContainer>
      </Dialog>
    </>
  );
};

UploadDamageImageDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // damageDetails: PropTypes.any,
};

export default UploadDamageImageDialog;
